import { useQuery } from "@tanstack/react-query";
import type { MeResponse } from "@shared/types";

/** The key every consumer of `/api/v2/me` shares, so one fetch serves them all. */
export const ME_QUERY_KEY = ["/api/v2/me"] as const;

/**
 * #419: how often `/api/v2/me` is read again while any instance it lists is still provisioning, so
 * the landing opens one the moment it is active and its "Provisioning" badge goes, without a
 * reload. Off once every listed instance is active or its setup failed (`setup_failed` waits for
 * a PlatformAdmin's Retry, not for time), so the usual five minute cache applies.
 */
export const ME_REFETCH_WHILE_PROVISIONING_MS = 15_000;

/** The refetch interval for the data `/me` last answered (`ME_REFETCH_WHILE_PROVISIONING_MS`). */
export function meRefetchInterval(data: MeResponse | undefined): number | false {
  return data?.instances.some((instance) => instance.status === "provisioning")
    ? ME_REFETCH_WHILE_PROVISIONING_MS
    : false;
}

/**
 * Who is signed in, what they may do, and which instances they can reach.
 *
 * `enabled` is the caller's business: the shell only asks once the auth layer
 * reports a session, so an anonymous visitor never fires a 401.
 */
export function useMe(enabled: boolean) {
  return useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    enabled,
    // Identity and grants change rarely, and an admin revoking access is
    // enforced server-side on the next request regardless of this cache.
    staleTime: 5 * 60_000,
    // #419: an instance still being set up is read again until it is active.
    refetchInterval: (query) => meRefetchInterval(query.state.data),
  });
}
